import { useSyncExternalStore } from 'react';
import { getProjectSnapshot } from './projectStore';

export const LIVE_PLAYLIST_STORAGE_KEY = 'lowerThirds.livePlaylist.v3';
export const LEGACY_LIVE_PLAYLIST_V2_STORAGE_KEY = 'lowerThirds.livePlaylist.v2';
export const LEGACY_LIVE_RUNDOWN_STORAGE_KEY = 'lowerThirds.liveRundown.v1';

const subscribers = new Set();
let snapshot = [];
let snapshotRaw = null;
let snapshotPeopleSignature = null;

const notify = () => {
  subscribers.forEach((listener) => listener());
};

const createLivePlaylistItemId = () => {
  const randomUuid = typeof window !== 'undefined' && window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : null;
  if (typeof randomUuid === 'string' && randomUuid.trim()) return randomUuid;
  return `playlist-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const createLivePlaylistItem = (personId) => ({
  id: createLivePlaylistItemId(),
  personId,
  mirroredOverride: null,
});

const arePlaylistItemsEqual = (left = [], right = []) => (
  left.length === right.length
    && left.every((item, index) => (
      item?.id === right[index]?.id
      && item?.personId === right[index]?.personId
      && item?.mirroredOverride === right[index]?.mirroredOverride
    ))
);

const buildSeedPlaylist = (people = []) => (
  people.map((person) => createLivePlaylistItem(person.id))
);

const sanitizeLivePlaylistItems = (items, people) => {
  const peopleIds = new Set(people.map((person) => person.id));
  const seenItemIds = new Set();
  return (Array.isArray(items) ? items : [])
    .map((item) => {
      const personId = typeof item?.personId === 'string' ? item.personId : null;
      if (!personId || !peopleIds.has(personId)) return null;
      let id = typeof item?.id === 'string' && item.id.trim() ? item.id.trim() : createLivePlaylistItemId();
      if (seenItemIds.has(id)) id = createLivePlaylistItemId();
      seenItemIds.add(id);
      const mirroredOverride = item?.mirroredOverride === true || item?.mirroredOverride === false
        ? item.mirroredOverride
        : null;
      return { id, personId, mirroredOverride };
    })
    .filter(Boolean);
};

const persistSnapshot = (items) => {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  const raw = JSON.stringify(items);
  window.localStorage.setItem(LIVE_PLAYLIST_STORAGE_KEY, raw);
  window.localStorage.removeItem(LEGACY_LIVE_PLAYLIST_V2_STORAGE_KEY);
  window.localStorage.removeItem(LEGACY_LIVE_RUNDOWN_STORAGE_KEY);
  snapshotRaw = raw;
  return raw;
};

const cacheSnapshot = (items, peopleSignature, raw = snapshotRaw) => {
  snapshot = items;
  snapshotPeopleSignature = peopleSignature;
  snapshotRaw = raw;
};

const readSeededPlaylist = (people, peopleSignature) => {
  const seeded = buildSeedPlaylist(people);
  if (typeof window !== 'undefined' && window.localStorage) {
    const raw = persistSnapshot(seeded);
    window.localStorage.removeItem(LEGACY_LIVE_PLAYLIST_V2_STORAGE_KEY);
    window.localStorage.removeItem(LEGACY_LIVE_RUNDOWN_STORAGE_KEY);
    cacheSnapshot(seeded, peopleSignature, raw);
    return seeded;
  }
  cacheSnapshot(seeded, peopleSignature, null);
  return seeded;
};

const migrateLegacyPlaylist = (legacyRaw, people, peopleSignature) => {
  try {
    const legacyIds = JSON.parse(legacyRaw);
    const migrated = sanitizeLivePlaylistItems(
      (Array.isArray(legacyIds) ? legacyIds : []).map((personId) => ({ personId })),
      people,
    );
    if (typeof window !== 'undefined' && window.localStorage) {
      const raw = persistSnapshot(migrated);
      window.localStorage.removeItem(LEGACY_LIVE_PLAYLIST_V2_STORAGE_KEY);
      window.localStorage.removeItem(LEGACY_LIVE_RUNDOWN_STORAGE_KEY);
      cacheSnapshot(migrated, peopleSignature, raw);
      return migrated;
    }
    cacheSnapshot(migrated, peopleSignature, null);
    return migrated;
  } catch {
    return readSeededPlaylist(people, peopleSignature);
  }
};

export const getLivePlaylistSnapshot = () => {
  const project = getProjectSnapshot();
  const people = Array.isArray(project?.people) ? project.people : [];
  const peopleSignature = people.map((person) => person.id).join('|');
  if (typeof window === 'undefined' || !window.localStorage) {
    const fallback = snapshotPeopleSignature === peopleSignature && snapshot.length
      ? sanitizeLivePlaylistItems(snapshot, people)
      : buildSeedPlaylist(people);
    cacheSnapshot(fallback, peopleSignature, null);
    return fallback;
  }

  const raw = window.localStorage.getItem(LIVE_PLAYLIST_STORAGE_KEY);
  if (!raw) {
    const legacyV2Raw = window.localStorage.getItem(LEGACY_LIVE_PLAYLIST_V2_STORAGE_KEY);
    if (legacyV2Raw) {
      try {
        const parsedLegacyV2 = JSON.parse(legacyV2Raw);
        const migrated = sanitizeLivePlaylistItems(parsedLegacyV2, people);
        const nextRaw = persistSnapshot(migrated);
        window.localStorage.removeItem(LEGACY_LIVE_PLAYLIST_V2_STORAGE_KEY);
        window.localStorage.removeItem(LEGACY_LIVE_RUNDOWN_STORAGE_KEY);
        cacheSnapshot(migrated, peopleSignature, nextRaw);
        return migrated;
      } catch {
        window.localStorage.removeItem(LEGACY_LIVE_PLAYLIST_V2_STORAGE_KEY);
      }
    }
    const legacyRaw = window.localStorage.getItem(LEGACY_LIVE_RUNDOWN_STORAGE_KEY);
    if (legacyRaw) return migrateLegacyPlaylist(legacyRaw, people, peopleSignature);
    return readSeededPlaylist(people, peopleSignature);
  }

  if (raw === snapshotRaw && peopleSignature === snapshotPeopleSignature) return snapshot;

  try {
    const parsed = JSON.parse(raw);
    const sanitized = sanitizeLivePlaylistItems(parsed, people);
    const nextRaw = JSON.stringify(sanitized);
    if (nextRaw !== raw) {
      window.localStorage.setItem(LIVE_PLAYLIST_STORAGE_KEY, nextRaw);
      cacheSnapshot(sanitized, peopleSignature, nextRaw);
      return sanitized;
    }
    cacheSnapshot(sanitized, peopleSignature, raw);
    return sanitized;
  } catch {
    return readSeededPlaylist(people, peopleSignature);
  }
};

export const replaceLivePlaylist = (playlistItems) => {
  const people = getProjectSnapshot().people;
  const next = sanitizeLivePlaylistItems(playlistItems, people);
  const peopleSignature = people.map((person) => person.id).join('|');
  const nextRaw = typeof window !== 'undefined' && window.localStorage
    ? persistSnapshot(next)
    : null;
  cacheSnapshot(next, peopleSignature, nextRaw);
  notify();
  return next;
};

export const subscribeLivePlaylistStore = (listener) => {
  subscribers.add(listener);
  const handleStorage = (event) => {
    if (
      event.key === LIVE_PLAYLIST_STORAGE_KEY
      || event.key === LEGACY_LIVE_PLAYLIST_V2_STORAGE_KEY
      || event.key === LEGACY_LIVE_RUNDOWN_STORAGE_KEY
    ) {
      notify();
    }
  };
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', handleStorage);
  }
  return () => {
    subscribers.delete(listener);
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', handleStorage);
    }
  };
};

export const useLivePlaylistSnapshot = () => (
  useSyncExternalStore(subscribeLivePlaylistStore, getLivePlaylistSnapshot, getLivePlaylistSnapshot)
);

export const __private__ = {
  arePlaylistItemsEqual,
  buildSeedPlaylist,
  sanitizeLivePlaylistItems,
};
